import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { ProgressComponent } from '../../progress/progress.component';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { QuestionItem } from '../../../models/question-item.model';
import { QuestionType } from './question-type.enum';
import { HasCloseEvent } from '../../../interfaces/has-close-event.interface';
import { DistributionBarComponent } from '../../distribution-bar/distribution-bar.component';
import { QuestionService } from '../../../services/question.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-question-list',
  standalone: true,
  imports: [
    ProgressComponent,
    NgIf,
    SvgIconComponent,
    NgForOf,
    NgClass,
    DistributionBarComponent,
    TranslocoDirective
  ],
  templateUrl: './question-list.component.html',
  styleUrl: './question-list.component.scss'
})
export class QuestionListComponent implements OnInit, HasCloseEvent {
  public closeEvent: EventEmitter<boolean> = new EventEmitter<boolean>()
  public data: QuestionItem[] = [];
  protected readonly: boolean = false;

  protected questionList: QuestionItem[] = [];
  protected progress: number = 0;
  protected answeredQuestionList: QuestionItem[] = [];
  protected unAnsweredQuestionList: QuestionItem[] = [];
  protected filterOptions: FilterOption[] = [];

  private questionService = inject(QuestionService);
  private translocoService = inject(TranslocoService);

  ngOnInit() {
    this.questionList = this.data;
    this.questionList.sort((a, b) => a.number - b.number)
    this.answeredQuestionList = this.questionList.filter(x => x.state !== QuestionType.Unknown);
    this.unAnsweredQuestionList = this.questionList.filter(x => x.state === QuestionType.Unknown);
    this.filterOptions = this.getFilterOptions();
    this.progress = this.calcProgress()
  }

  private calcProgress(): number {
    let all = this.questionList.length;
    let missing = this.questionList.filter(x => x.state === QuestionType.Unknown).length;
    return (all - missing) / all * 100;
  }

  protected getFilterOptions() {
    let options = this.questionList.map(x => x.state).filter((value, index, array) => array.indexOf(value) === index).sort((a, b) => a - b);
    return options.map(x => ({ option: x, active: true } as FilterOption));
  }

  protected setClass(state: QuestionType): string {
    switch (state) {
      case QuestionType.Ok:
        return 'ok';
      case QuestionType.Nok:
        return 'nok';
      case QuestionType.NotRelevant:
        return 'not-relevant';
      case QuestionType.Unknown:
        return 'unknown';
    }
  }

  protected setSelected(state: QuestionType): string {
    if (this.filterOptions.find(o => o.option === state)?.active) {
      return 'selected';
    }
    return '';
  }

  protected setDisplayName(state: QuestionType): string {
    switch (state) {
      case QuestionType.Ok:
        return this.translocoService.translate('STATES.OK');
      case QuestionType.Nok:
        return this.translocoService.translate('STATES.NOK');
      case QuestionType.NotRelevant:
        return this.translocoService.translate('STATES.NOT-RELEVANT');
      case QuestionType.Unknown:
        return this.translocoService.translate('STATES.UNKNOWN');
    }
  }

  protected setFilterList(state: FilterOption): void {
    if (this.filterOptions.every(o => o.active)) {
      this.filterOptions.forEach(o => o.active = false);
    }
    let index = this.filterOptions.findIndex(value => value.option === state.option);
    this.filterOptions[index].active = !this.filterOptions[index].active;

    this.answeredQuestionList = this.questionList.filter(x => x.state !== QuestionType.Unknown && this.filterOptions.find(o => o.option === x.state)?.active);
    this.unAnsweredQuestionList = this.questionList.filter(x => x.state === QuestionType.Unknown && this.filterOptions.find(o => o.option === x.state)?.active);
  }

  protected openQuestion(id: string) {
    this.questionService.selectedId.next(id);
    this.closeEvent.emit(true);
  }
}

class FilterOption {
  public option: QuestionType = QuestionType.Ok;
  public active: boolean = false;
}
